import type { ReactNode } from "react";
import { Globe } from "lucide-react";
import { NavLink, useParams } from "react-router-dom";

import { PageHeader } from "@/shared/ui/PageHeader";
import { cn } from "@/shared/lib/utils";

import type { SiteCard } from "../api/types";
import { sitePath, STEP_PATHS, STEPS, stepOf, type StepSite } from "../model/siteStep";
import { SiteSteps } from "./SiteSteps";
import { FixtureBanner } from "./WebsiteShell";

/**
 * 站点级页头(生成 / 预览调整 / 上线三段 + 高级编辑各页共用):「← 返回」+「{站点名} · {页名}」+ 三段条 + 站点页标签。
 *
 * ★ `backTo` 是 `useBackNav` 的三层兜底:有来源回来源、有栈退一步、深链落地退到 `/ai-website`。
 * ★ `beforeLeave`:页面有未保存改动时,「← 返回」先过这道闸(回 false 留在本页)。
 * ★ 当前步不从路由猜(在采集页 ≠ 站点处于采集步:已上线的站也能回来改资料),从站点状态派生。
 */
export function SiteShell({
  site,
  pageTitle,
  subtitle,
  actions,
  beforeLeave,
  children,
}: {
  site: Pick<SiteCard, "name"> & StepSite;
  pageTitle: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  beforeLeave?: () => boolean | Promise<boolean>;
  children: ReactNode;
}) {
  return (
    <div className="space-y-6">
      <PageHeader
        icon={Globe}
        backTo="/ai-website"
        title={`${site.name} · ${pageTitle}`}
        subtitle={subtitle}
        actions={actions}
        beforeBack={beforeLeave}
      />
      <FixtureBanner />
      <SiteSteps current={stepOf(site)} />
      <SiteTabs site={site} />
      {children}
    </div>
  );
}

/**
 * 站点页标签(1010 三段式):主流程「生成 / 预览调整 / 上线」+「高级编辑」(原资料采集 / 内容编辑 / 域名绑定 / 新闻栏,保留不删)。
 * 站点 id 从路由 `/ai-website/sites/:id/*` 取(调用方不用改)。新闻栏只对上线过 / 有预览的站有意义,没生成过的站不出。
 * ★ 这是导航(可点),与上面不可点的步骤条是两件事:步骤条说「站点处于哪一段」,标签说「我在看哪一页」。
 */
function SiteTabs({ site }: { site: StepSite }) {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  const hasOutput = site.hasDraft || site.currentVersionNo != null || site.status !== "draft";
  const main = STEP_PATHS.map((p, i) => ({ to: sitePath(id, p), label: STEPS[i] }));
  const advanced = [
    { to: sitePath(id, "collect"), label: "资料采集" },
    { to: sitePath(id, "edit"), label: "内容编辑" },
    { to: sitePath(id, "domain"), label: "域名绑定" },
    ...(hasOutput ? [{ to: sitePath(id, "news"), label: "新闻栏" }] : []),
  ];
  const tab = ({ isActive }: { isActive: boolean }) =>
    cn(
      "-mb-px border-b-2 px-3 py-1.5 text-sm",
      isActive
        ? "border-primary font-semibold text-foreground"
        : "border-transparent text-muted-foreground hover:text-foreground"
    );
  return (
    <nav
      className="flex flex-wrap items-end gap-1 border-b border-border"
      aria-label="站点页"
      data-site-tabs=""
    >
      {main.map((t) => (
        <NavLink key={t.to} to={t.to} className={tab}>
          {t.label}
        </NavLink>
      ))}
      <span className="ml-auto self-center px-2 text-2xs text-muted-foreground" aria-hidden>
        高级编辑
      </span>
      <span role="group" aria-label="高级编辑" className="flex flex-wrap gap-1">
        {advanced.map((t) => (
          <NavLink key={t.to} to={t.to} className={(a) => cn(tab(a), "text-xs")}>
            {t.label}
          </NavLink>
        ))}
      </span>
    </nav>
  );
}
